import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { reloadForStaleBuild } from '@/lib/staleBuild';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** When this key changes, the error state automatically resets (e.g. pass location.pathname). */
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // A screen of an older build than the server now serves (a tab opened before a deploy): reload once to get the new
    // one, instead of an error "Try Again" cannot fix (lib/staleBuild.ts).
    if (reloadForStaleBuild(error)) return;
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    // Auto-reset when the resetKey changes (route navigation)
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex min-h-[400px] items-center justify-center px-6">
          <div className="w-full max-w-md text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-danger/10">
              <AlertTriangle className="h-7 w-7 text-danger" />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-foreground">
              Something went wrong
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              An unexpected error occurred. Try refreshing the page or go back to the dashboard.
            </p>
            {this.state.error && (
              <details className="mt-4 rounded-lg border bg-muted/30 p-3 text-left">
                <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                  Error details
                </summary>
                <pre className="mt-2 overflow-x-auto text-xs text-danger">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                onClick={this.handleReset}
                className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent press-scale"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>
              <a
                href="/dashboard"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 press-scale"
              >
                <Home className="h-4 w-4" />
                Dashboard
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
